import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import getTicketsList from '../../../api/graphql/queries/getTicketsList';
import getSellers from '../../../api/graphql/queries/getSellers';
import getPostsByType from '../../../api/graphql/queries/getPostsByType';
import postAddTicket from '../../../api/graphql/mutations/postAddTicket';
import postEditTicket from '../../../api/graphql/mutations/postEditTicket';
import postDeleteTicket from '../../../api/graphql/mutations/postDeleteTicket';
import catalogUpdateRun from '../../../api/graphql/mutations/catalogUpdateRun';
import getTourPostSlugs from '../../../api/graphql/queries/getTourPostSlugs';
import { mayNeedTourPrice, isUnpricedTicket } from '../../../utils/ticketPriceStatus';

import LoadIcon from '../../LoadIcon';

import Filter from '../../Tabs/Filter';
import DataTable from '../../Table/DataTable';
import ticketColumns from './ticketColumns';
// import TabItem from '../../Tabs/TabItem';
// import TicketForm from './TicketForm';
import Form from './Form';
import Modal from '../../Modal/Modal';
import slugify from '../../../utils/slugify';
// import DraggableList from '../../DraggableList';

const itemName = 'ticketSlugName';
const DEFAULT_ROWS_PER_PAGE = 50;

const ISSUE_LABELS = {
  'no-price': 'Tickets with no price',
  'no-event': 'Tickets no event lists',
};

const debounce = (func, delay) => {
  let inDebounce;
  return (...args) => {
    const context = this;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  };
};

class TicketContainer extends Component {
  state = {
    filterText: '',
    isCreateModalOn: false,
    isDisplayModalOn: false,
    isModifyModalOn: false,
    isDeleteModalOn: false,
    formOutput: {},
    currItemData: [],
    toggleDeleteInfoScreen: false,
    // initialItems: [],
    filteredItems: [],
    isLoading: true,
    canItemBeDeleted: false,
    ticketSlugNames: [],
    sellersData: [],
    sellersSlugs: [],
    routePosts: [],
    isSyncingCatalog: false,
    catalogSyncResult: null,
    tourPostSlugs: null,
    page: 0,
    total: 0,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    sorting: ['createdAt', -1],
    issue: null,
  };

  // Any new search starts again at the first page: staying on page 7 of the
  // previous result set would land the editor on an empty screen.
  updateSearch = debounce(async (val) => {
    this.setState({ filterText: val }, () => this.loadPage(0));
  }, 500);

  /*
  One page of tickets, newest first. The list used to render nothing until four
  characters were typed, because the query returned the whole collection and
  there was nothing else holding it back; it pages now, so the first render can
  show real content and a search is a filter rather than a precondition.
  */
  loadPage = async (page = 0) => {
    const { filterText, issue, rowsPerPage, sorting } = this.state;
    this.setState({ isLoading: true });
    const result = await getTicketsList((filterText || '').toLowerCase(), {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      issue,
      sortField: sorting[0],
      sortOrder: sorting[1],
    });
    this.setState({
      filteredItems: (result && result.tickets) || [],
      total: (result && result.total) || 0,
      page,
      isLoading: false,
    });
  };

  // Narrowing to a problem is a different result set, so it starts at page 1
  // for the same reason a new search does.
  toggleIssue = (next) => {
    this.setState(({ issue }) => ({ issue: issue === next ? null : next }), () => this.loadPage(0));
  };

  // The table owns page size and sort order; both reset to the first page,
  // because a different page size or order makes the old offset meaningless.
  setRowsPerPage = (rowsPerPage) => {
    this.setState({ rowsPerPage }, () => this.loadPage(0));
  };

  setSorting = (sorting) => {
    this.setState({ sorting }, () => this.loadPage(0));
  };

  /*
  Everything the screen needs, at once.

  These used to be awaited one after another, so the list waited on the route
  posts and the sellers — neither of which it renders — before it could even ask
  for its first page. They are independent, so they go together and the list
  paints as soon as its own query lands.
  */
  componentDidMount = () => {
    this.loadPage(0);
    this.getPosts();
    this.getSellers();
    // Which posts are tours, so an unpriced tours ticket can be flagged in the
    // list. Advisory: if this never arrives, no row is flagged.
    getTourPostSlugs().then(tourPostSlugs => this.setState({ tourPostSlugs }));
  };

  // The list mixes every kind of ticket, so a row attached to a post that is
  // not a tour stays unmarked — one left at 0 on purpose would just be noise.
  ticketNote = (ticket) => {
    const { tourPostSlugs } = this.state;
    return mayNeedTourPrice(ticket, tourPostSlugs) && isUnpricedTicket(ticket) ? 'no price' : null;
  };

  getPosts = async () => {
    const res = await getPostsByType('route');
    try {
      if (res) {
        const { posts } = res;
        const routePosts = posts.map(post => post.slugName);
        this.setState({
          routePosts,
        });
      }
    } catch {
      err => console.log(err, ' => getPostsByCategory');
    }
  };

  getSellers = async () => {
    const sellersData = await getSellers();
    if (sellersData) {
      console.log('sellersData', sellersData);
      const sellersSlugs = sellersData.map(i => (({ sellerSlugName, sellerName }) => ({ text: sellerName, value: sellerSlugName }))(i));
      this.setState({ sellersData, sellersSlugs });
    } else {
      console.log('sellersData = null');
    }
  };


  handleClick = (action, item) => {
    console.log(`And the action is: ${action}, for item ${item}`);

    switch (action) {
      case 'create':
        this.toggleCreateModal();
        break;
      case 'display':
        this.toggleDisplayModal(item);
        break;
      case 'modify':
        this.toggleModifyModal(item);
        break;
      case 'delete':
        this.toggleDeleteModal(item);
        break;
      case 'closeDeleteItem':
        this.toggleDeleteInfoScreen();
        break;
      default:
        console.log('wrong action!');
        break;
    }
  };

  toggleCreateModal = () => {
    this.setState({
      isCreateModalOn: !this.state.isCreateModalOn,
    });
  };

  toggleDisplayModal = (item) => {
    const { filteredItems } = this.state;
    const currItemData = filteredItems.filter(el => el[itemName] === item);
    this.setState({
      isDisplayModalOn: !this.state.isDisplayModalOn,
      currItemData,
    });
  };

  toggleModifyModal = (item) => {
    const { filteredItems } = this.state;
    const currItemData = filteredItems.filter(el => el[itemName] === item);
    this.setState({
      isModifyModalOn: !this.state.isModifyModalOn,
      currItemData,
    });
  };

  toggleDeleteModal = (item) => {
    const { filteredItems } = this.state;
    const currItemData = filteredItems.filter(el => el[itemName] === item);
    this.setState({
      isDeleteModalOn: !this.state.isDeleteModalOn,
      currItemData,
    });
  };

  toggleDeleteInfoScreen = () => this.setState({ toggleDeleteInfoScreen: !this.state.toggleDeleteInfoScreen });

  formOutput = (formOutput, action) => {
    this.setState({ formOutput });
    console.log('formOutput', formOutput);

    const { history } = this.props;

    if (action === 'create') {
      postAddTicket({ ...formOutput }).then((postAddTicketResultData) => {
        if (postAddTicketResultData) {
          if (postAddTicketResultData.isAuth) {
            if (postAddTicketResultData.isAdded) {
              console.log(`postAddTicket - Successfully added ticket with ticketSlugName ${postAddTicketResultData.ticketSlugName}!`);
              this.loadPage(0);
            } else {
              console.log('postAddTicket - Failed to add ticket! Missing fields or ticketSlugName exists');
            }
          } else {
            console.log('postAddTicket - User authentication failed!');
            history.push('/login');
          }
        } else {
          console.log('postAddTicketResultData = null');
        }
      });
    } else if (action === 'modify') {
      postEditTicket({ ...formOutput }).then((postEditTicketResultData) => {
        if (postEditTicketResultData) {
          if (postEditTicketResultData.isAuth) {
            if (postEditTicketResultData.isEdited) {
              console.log(`postEditTicket - Successfully edited ticket with ticketSlugName ${postEditTicketResultData.ticketSlugName}!`);
              const { page } = this.state;
              this.loadPage(page);
            } else {
              console.log('postEditTicket - Failed to edit ticket! Missing fields or ticketSlugName does not exist');
            }
          } else {
            console.log('postEditTicket - User authentication failed!');
            history.push('/login');
          }
        } else {
          console.log('postEditTicketResultData = null');
        }
      });
    }
  };

  handleDelete = (itemId, itemSlug) => {
    console.log(`the item ${itemSlug} will be deleted`);

    const { history } = this.props;

    const formData = {
      id: itemId,
      ticketSlugName: slugify(itemSlug.toLowerCase()),
    };

    postDeleteTicket({ ...formData }).then((postDeleteTicketResultData) => {
      if (postDeleteTicketResultData) {
        if (postDeleteTicketResultData.isAuth) {
          if (postDeleteTicketResultData.isDeleted) {
            console.log(`postDeleteTicket - Successfully deleted ticket with ticketSlugName ${postDeleteTicketResultData.ticketSlugName}!`);
            this.toggleDeleteInfoScreen();
            this.setState({ canItemBeDeleted: true });
            const { page } = this.state;
            this.loadPage(page);
          } else {
            if (postDeleteTicketResultData.isUsed.eventSlugNames.length === 0) {
              console.log('postDeleteTicket - Failed to delete ticket! Ticket does not exist.');
            } else if (postDeleteTicketResultData.isUsed.eventSlugNames.length > 0) {
              console.log(`postDeleteTicket - Failed to delete ticket! Ticket is used in eventSlugNames: ${postDeleteTicketResultData.isUsed.eventSlugNames}`);
              this.toggleDeleteInfoScreen();
              this.setState({ canItemBeDeleted: false });
            }
          }
        } else {
          console.log('postDeleteTicket - User authentication failed!');
          history.push('/login');
        }
      } else {
        console.log('postDeleteTicketResultData = null');
      }
    });
  };

  // Push the ticket catalog to Braze on demand. Runs a full reconciliation so any
  // item that drifted (e.g. a venue city edited after its last sync) is re-pushed.
  syncCatalog = async () => {
    this.setState({ isSyncingCatalog: true, catalogSyncResult: null });
    const res = await catalogUpdateRun({ fullSync: true });

    let message;
    if (!res) message = 'Catalog sync failed — check your connection and admin login.';
    else if (!res.isAuth) message = 'Not authorized — admin role required.';
    else if (!res.success) message = `Catalog sync failed: ${res.error || 'unknown error'}`;
    else message = `Catalog synced (${res.syncType || 'ok'}) — ${res.totalItems} item(s)${res.staleItemsDeleted ? `, ${res.staleItemsDeleted} removed` : ''}${res.duration ? ` in ${res.duration}` : ''}.`;

    this.setState({ isSyncingCatalog: false, catalogSyncResult: message });
  };

  render() {
    const {
      filterText, isCreateModalOn, isDisplayModalOn, isDeleteModalOn, isModifyModalOn, currItemData, canItemBeDeleted, toggleDeleteInfoScreen, filteredItems, isLoading, sellersData, sellersSlugs, routePosts, isSyncingCatalog, catalogSyncResult, page, total, issue, rowsPerPage, sorting,
    } = this.state;

    return (
      <div className="w-full bg-white">
        <div className="flex justify-between mb-4">
          <span className="text-americaMonoBold text-26">Tickets</span>
          <div className="flex items-center">
            <button
              type="button"
              disabled={isSyncingCatalog}
              title="Push all tickets to the Braze catalog (full reconciliation)"
              className="font-americaMonoBold text-xs px-6 py-2 rounded-full border border-black text-black uppercase h-10 flex items-center mr-3"
              onClick={this.syncCatalog}
            >
              {isSyncingCatalog ? 'Syncing…' : 'Sync to Braze'}
            </button>
            <button
              type="button"
              className="font-americaMonoBold text-xs pr-6 pl-8 py-2 rounded-full bg-black text-white uppercase h-10 flex items-center"
              onClick={() => {
                this.handleClick('create');
              }}
            >
              Create Ticket
              {' '}
              <span className="font-icon icon-add-button text-white font-black pl-3 text-base" />
            </button>
          </div>
        </div>
        {catalogSyncResult && (
          <div className="mb-4 text-xs text-gray-700">{catalogSyncResult}</div>
        )}
        {isCreateModalOn && (
          <Modal title="Add Ticket" handleClick={this.handleClick} type="create">
            {
              // to use the new ticket modal change instances of TicketForm to Form
            }
            <Form routePosts={routePosts} handleClick={this.handleClick} retrieveOutput={this.formOutput} action="create" />
          </Modal>
        )}
        {isDisplayModalOn && (
          <Modal title="Display Ticket" handleClick={this.handleClick} type="display">
            <Form handleClick={this.handleClick} formInput={currItemData[0]} action="display" />
          </Modal>
        )}
        {isModifyModalOn && (
          <Modal title="Modify Ticket" handleClick={this.handleClick} type="modify">
            <Form routePosts={routePosts} handleClick={this.handleClick} retrieveOutput={this.formOutput} formInput={currItemData[0]} action="modify" sellersData={sellersData} sellersSlugs={sellersSlugs} />
          </Modal>
        )}
        {isDeleteModalOn && (
          <Modal title="Delete Ticket" handleClick={this.handleClick} type="delete">
            <Form handleClick={this.handleClick} formInput={currItemData[0]} handleDelete={this.handleDelete} canItemBeDeleted={canItemBeDeleted} toggleDeleteInfoScreen={toggleDeleteInfoScreen} closeToggleDelete={this.toggleDeleteInfoScreen} action="delete" />
          </Modal>
        )}
        <div className="mt-4">
          <Filter updateSearch={this.updateSearch} searchText={filterText} placeholder="Search tickets by name or slug" />
          <div className="flex items-center mb-4">
            <button
              type="button"
              title="Tickets that are shown but have no price"
              onClick={() => this.toggleIssue('no-price')}
              className={`font-americaMonoBold text-xs px-4 py-1 rounded-full uppercase border mr-2 ${issue === 'no-price' ? 'bg-black text-white border-black' : 'border-black text-black'}`}
            >
              No price
            </button>
            <button
              type="button"
              title="Tickets no event lists, so they appear on no page at all"
              onClick={() => this.toggleIssue('no-event')}
              className={`font-americaMonoBold text-xs px-4 py-1 rounded-full uppercase border ${issue === 'no-event' ? 'bg-black text-white border-black' : 'border-black text-black'}`}
            >
              No event
            </button>
          </div>
          {isLoading && <LoadIcon />}
          {!isLoading && (filteredItems && filteredItems.length
            ? (
              <DataTable
                tableData={filteredItems}
                columns={ticketColumns(this.handleClick, this.ticketNote)}
                total={total}
                page={page}
                setPage={this.loadPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={this.setRowsPerPage}
                sorting={sorting}
                setSorting={this.setSorting}
              />
            )
            : (
              <div>{issue || filterText ? 'No tickets match those filters' : 'No tickets yet'}</div>
            ))}
        </div>
      </div>
    );
  }
}

TicketContainer.propTypes = {
  history: PropTypes.shape().isRequired,
};

export default withRouter(TicketContainer);
